import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import { CollectionInfo } from "@redotech/shopify-storefront";
import { isDef } from "@redotech/util/checks";
import { memo, useMemo, useState } from "react";
import { useDebounce } from "usehooks-ts";
import { BadgeRedoListItem } from "../../../arbiter-components/list/redo-list";
import { RedoModal } from "../../../arbiter-components/modal/redo-modal";
import { RedoTagsDropdownInput } from "../../../arbiter-components/select-dropdown/redo-tags-dropdown-input";
import { Flex } from "../../../flex";

export const CollectionSelectionModal = memo(function CollectionSelectionModal({
  selectedCollections,
  onCollectionsSelected,
  onClose,
  searchCollections,
}: {
  selectedCollections: string[];
  onCollectionsSelected: (collections: string[]) => void;
  onClose: () => void;
  searchCollections: (params: { search: string }) => Promise<CollectionInfo[]>;
}) {
  const [localSelectedCollections, setLocalSelectedCollections] =
    useState<string[]>(selectedCollections);
  const [searchString, setSearchString] = useState("");

  const debouncedSearch = useDebounce(searchString, 500);

  const collectionsLoad = useLoad(
    async () => await searchCollections({ search: debouncedSearch }),
    [debouncedSearch, searchCollections],
  );

  const collectionOptions: BadgeRedoListItem<string>[] = useMemo(() => {
    return (
      collectionsLoad.value
        ?.filter(isDef)
        .map((collection) => ({
          type: "badge",
          id: collection.id,
          value: collection.id,
          badge: { text: collection.title, color: "gray" },
        })) ?? []
    );
  }, [collectionsLoad.value]);

  const selectedOptions = useMemo(() => {
    return collectionOptions.filter((option) =>
      localSelectedCollections.includes(option.value),
    );
  }, [collectionOptions, localSelectedCollections]);

  const handleSelectionChange = useHandler(
    (items: BadgeRedoListItem<string>[]) => {
      setLocalSelectedCollections(items.map((item) => item.value));
    },
  );

  const handleSave = useHandler(() => {
    onCollectionsSelected(localSelectedCollections);
  });

  return (
    <RedoModal
      modalSize="md"
      onModalCloseRequested={onClose}
      primaryButton={{ text: "Save", onClick: handleSave }}
      secondaryButton={{ text: "Cancel", onClick: onClose }}
      title="Select Collections"
    >
      <Flex dir="column" gap="lg" p="md">
        <RedoTagsDropdownInput<string>
          label="Collections"
          loading={collectionsLoad.pending}
          options={collectionOptions}
          placeholder="Select collections"
          searchPlaceholder="Search for collections"
          searchString={searchString}
          selectedOptions={selectedOptions}
          setSearchString={setSearchString}
          setSelectedOptions={handleSelectionChange}
          size="xs"
        />
      </Flex>
    </RedoModal>
  );
});
